import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Mail } from 'lucide-react'; // News icon

// Utility to get relative time (e.g., '2 hours ago')
function getRelativeTime(dateString) {
    const now = new Date();
    const published = new Date(dateString);
    const diffMs = now - published;
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return `${diffSec} seconds ago`;
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin} minutes ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr} hours ago`;
    const diffDay = Math.floor(diffHr / 24);
    return `${diffDay} days ago`;
}

const LearningContent = () => {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchArticles = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/trading/fresh-news/`);
                setArticles(response.data);
            } catch (err) {
                setError('Failed to load news.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchArticles();
    }, []);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-40">
                <svg className="animate-spin h-8 w-8 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                </svg>
                <span className="ml-3 text-primary-600 font-medium">Loading recent news...</span>
            </div>
        );
    }

    if (error) {
        return <div className="p-4 bg-red-100 text-red-700 rounded-lg text-center">{error}</div>;
    }

    return (
        <div className="p-4 bg-white dark:bg-gray-800 rounded-lg shadow-md">
            <div className="flex items-center mb-4">
                <Mail className="h-7 w-7 text-primary-600 mr-2" />
                <h2 className="text-2xl font-bold">Fresh Market News</h2>
            </div>
            {articles.length > 0 ? (
                <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2">
                    {articles.map((article, index) => {
                        const isRecent = (() => {
                            const published = new Date(article.published_at);
                            return (new Date() - published) < 24 * 60 * 60 * 1000;
                        })();
                        return (
                            <div key={index} className="bg-gray-50 dark:bg-gray-900 rounded-lg shadow hover:shadow-lg transition-all duration-200 border border-gray-200 dark:border-gray-700 p-4 flex flex-col h-full transform hover:scale-105">
                                {article.image_url && (
                                    <img src={article.image_url} alt="news" className="rounded mb-2 w-full h-40 object-cover" />
                                )}
                                <div className="flex items-center mb-2">
                                    <a href={article.url} target="_blank" rel="noopener noreferrer" className="text-lg font-semibold text-primary-700 dark:text-primary-400 hover:underline flex-1">
                                        {article.title}
                                    </a>
                                    {isRecent && (
                                        <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-semibold">Recent</span>
                                    )}
                                </div>
                                <p className="text-gray-700 dark:text-gray-300 text-sm mb-2">
                                    {article.description}
                                </p>
                                <div className="flex items-center justify-between mt-auto pt-2 text-xs text-gray-500 dark:text-gray-400">
                                    <span>{getRelativeTime(article.published_at)}</span>
                                    <span>Source: {article.source || 'Unknown'}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <p className="text-gray-500 text-center">No news found at the moment. Please check back later.</p>
            )}
        </div>
    );
};

export default LearningContent; 